import { yupResolver } from "@hookform/resolvers/yup";
import EditIcon from "@mui/icons-material/Edit";
import { DateRangeSharp } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  GlobalStyles,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import {
  createSupplier,
  getPaymentTypes,
  searchSuppliers,
  getTransTypes,
  updateSupplier,
} from "src/apis/controllers/expenses";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import RadioInput from "src/qubCommon/Inputs/RadioInput.jsx";
import CurrencyInput from "src/qubCommon/Inputs/CurrencyInput";
import {
  CssTextField,
  DateInput,
  Dropdown,
  TextInput,
} from "src/qubCommon/Inputs";
import {
  getResponseMessage,
  getResponsePayload,
  isErrorResponse,
} from "./headerPayload";
import ExpenseAttachments from "./ExpenseAttachments";
import SupplierDialog from "./SupplierDialog";

const normalizeMode = (mode) => mode?.toString?.().trim().toLowerCase() ?? "";

const isRequiredMode = (mode) => normalizeMode(mode) === "required";

const isHiddenMode = (mode) => normalizeMode(mode) === "hidden";

const isVisibleMode = (mode) => !!mode && !isHiddenMode(mode);

const hasSelectedValue = (value) =>
  value !== undefined && value !== null && value?.toString?.().trim?.() !== "";

const PAYMENT_MODE_PAID = "paid";
const PAYMENT_MODE_CREDIT = "credit";

const PAYMENT_MODE_OPTIONS = [
  { Value: PAYMENT_MODE_PAID, Label: "ΑΜΕΣΑ ΚΑΤΑ ΤΗΝ ΕΚΔΟΣΗ" },
  { Value: PAYMENT_MODE_CREDIT, Label: "ΜΕΤΑ ΤΗΝ ΕΚΔΟΣΗ" },
];

const CREATE_SUPPLIER_OPTION_ID = "__create_supplier__";

const CREATE_HEADER_ATTACHMENT_SETTINGS = {
  canAdd: true,
  canEdit: true,
  canDelete: true,
};

const normalizeSupplierSearch = (value) => value?.toString?.().trim?.() ?? "";

const parsePaymentAmountInput = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let nextValue = value
    .toString()
    .replace(/\s/g, "")
    .replace(/[^\d,.-]/g, "")
    .trim();
  if (!nextValue) return null;

  if (nextValue.includes(",") && nextValue.includes(".")) {
    nextValue = nextValue.replace(/\./g, "").replace(",", ".");
  } else {
    nextValue = nextValue.replace(",", ".");
  }

  const amount = Number(nextValue);
  return Number.isFinite(amount) ? amount : null;
};

const paymentAmountFormatter = new Intl.NumberFormat("el-GR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatPaymentAmount = (value) =>
  paymentAmountFormatter.format(Number(value ?? 0));

const formatPaymentAmountForInput = (value) => {
  const amount = parsePaymentAmountInput(value);
  return amount === null ? "" : formatPaymentAmount(amount);
};

const isTrueFlag = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const isFalseFlag = (value) =>
  value === false || value?.toString?.().trim?.().toLowerCase?.() === "false";

const getInitialPaidOnIssuePayment = (initialValues) => {
  if (!isTrueFlag(initialValues?.paidOnIssue)) return null;
  const payments = Array.isArray(initialValues?.payments)
    ? initialValues.payments
    : [];
  return payments[0] ?? null;
};

const getPaymentTypeIdFromPayment = (payment) => {
  if (!payment) return "";
  if (payment.cashAmount !== undefined && payment.cashAmount !== null) return 1;
  if (payment.cardAmount !== undefined && payment.cardAmount !== null) return 2;
  if (payment.bankAmount !== undefined && payment.bankAmount !== null) return 3;
  return "";
};

const getPaymentAmountFromPayment = (payment) => {
  if (!payment) return "";
  return (
    payment.cashAmount ??
    payment.cardAmount ??
    payment.bankAmount ??
    payment.amount ??
    ""
  );
};

const getInitialPaymentTypeId = (initialValues, paidOnIssuePayment) =>
  hasSelectedValue(initialValues?.paymentTypeId)
    ? initialValues.paymentTypeId
    : getPaymentTypeIdFromPayment(paidOnIssuePayment);

const getInitialPaymentAmount = (initialValues, paidOnIssuePayment) => {
  const candidates = [
    initialValues?.paymentAmount,
    initialValues?.paidAmount,
    getPaymentAmountFromPayment(paidOnIssuePayment),
  ];

  return candidates.find((value) => hasSelectedValue(value)) ?? "";
};

const getInitialPaymentMode = (initialValues) => {
  if (hasSelectedValue(initialValues?.paymentMode)) {
    return initialValues.paymentMode;
  }
  if (isTrueFlag(initialValues?.paidOnIssue)) return PAYMENT_MODE_PAID;
  if (isFalseFlag(initialValues?.paidOnIssue)) return PAYMENT_MODE_CREDIT;
  return "";
};

const normalizeAfm = (value) =>
  value?.toString?.().replace(/\D/g, "").slice(0, 9) ?? "";

const isCreateSupplierOption = (option) =>
  option?.id === CREATE_SUPPLIER_OPTION_ID || option?.isCreateOption === true;

const makeCreateSupplierOption = (searchText) => ({
  id: CREATE_SUPPLIER_OPTION_ID,
  isCreateOption: true,
  searchText,
});

const getSupplierOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  if (isCreateSupplierOption(option)) {
    return option.searchText
      ? `Δημιουργία νέου προμηθευτή: ${option.searchText}`
      : "Δημιουργία νέου προμηθευτή";
  }

  const title = option.onomasia || option.commerTitle || "Προμηθευτής";
  return [title, option.afm].filter(Boolean).join(" - ");
};

const HeaderDatePickerButton = forwardRef(({ onClick }, ref) => (
  <IconButton
    aria-label="Άνοιγμα ημερολογίου"
    onClick={onClick}
    ref={ref}
    tabIndex={-1}
  >
    <DateRangeSharp />
  </IconButton>
));

HeaderDatePickerButton.displayName = "HeaderDatePickerButton";

const requiredByMode = (modeField, message) =>
  yup
    .mixed()
    .nullable()
    .test("required-by-mode", message, (value, context) => {
      const mode = context?.parent?.[modeField];
      if (!isRequiredMode(mode)) return true;
      if (value instanceof Date) return true;
      return hasSelectedValue(value);
    });

const headerSchema = yup.object({
  transType: yup
    .mixed()
    .nullable()
    .test(
      "trans-type-required",
      "Απαιτείται επιλογή.",
      (value, context) =>
        hasSelectedValue(value) ||
        hasSelectedValue(context?.parent?.initialTransType)
    ),
  transDate: yup
    .mixed()
    .test("trans-date-required", "Απαιτείται ημερομηνία.", (value) => !!value),
  dateFromMode: yup.string().nullable(),
  dateToMode: yup.string().nullable(),
  dateFrom: requiredByMode("dateFromMode", "Απαιτείται ημερομηνία από."),
  dateTo: requiredByMode("dateToMode", "Απαιτείται ημερομηνία έως."),
  transNumber: yup.string().trim().required("Απαιτείται αριθμός."),
  transTypeSupplierMode: yup.string().nullable(),
  supplierAfm: yup.string().nullable(),
  supplierId: yup
    .mixed()
    .nullable()
    .test(
      "supplier-selected",
      "Αναζητήστε ή δημιουργήστε προμηθευτή.",
      (value, context) => {
        const mode = context?.parent?.transTypeSupplierMode;

        if (isHiddenMode(mode)) return true;
        if (isRequiredMode(mode)) return !!value;
        return true;
      }
    ),
  paymentMode: yup
    .string()
    .nullable()
    .test(
      "payment-mode-required",
      "Επιλεξτε τρόπο πληρωμής.",
      (value) => hasSelectedValue(value)
    ),
  paymentTypeId: yup
    .mixed()
    .nullable()
    .test(
      "payment-type-required",
      "Απαιτείται τρόπος πληρωμής.",
      (value, context) => {
        if (context?.parent?.isCreateMode === false) return true;
        if (context?.parent?.paymentMode !== PAYMENT_MODE_PAID) return true;
        return hasSelectedValue(value);
      }
    ),
  paymentAmount: yup
    .mixed()
    .nullable()
    .test(
      "payment-amount-required",
      "Απαιτείται ποσό πληρωμής.",
      (value, context) => {
        if (context?.parent?.isCreateMode === false) return true;
        if (context?.parent?.paymentMode !== PAYMENT_MODE_PAID) return true;
        return hasSelectedValue(value);
      }
    )
    .test(
      "payment-amount-valid",
      "Το ποσό πληρωμής δεν είναι έγκυρο.",
      (value, context) => {
        if (context?.parent?.isCreateMode === false) return true;
        if (context?.parent?.paymentMode !== PAYMENT_MODE_PAID) return true;
        if (!hasSelectedValue(value)) return true;
        return parsePaymentAmountInput(value) !== null;
      }
    )
    .test(
      "payment-amount-non-negative",
      "Το ποσό πληρωμής δεν μπορεί να είναι αρνητικό.",
      (value, context) => {
        if (context?.parent?.isCreateMode === false) return true;
        if (context?.parent?.paymentMode !== PAYMENT_MODE_PAID) return true;
        if (!hasSelectedValue(value)) return true;
        const amount = parsePaymentAmountInput(value);
        return amount !== null && amount >= 0;
      }
    )
    .test(
      "payment-amount-cash-limit",
      "Το ποσό μετρητών πρέπει να είναι μικρότερο από 500,00 €.",
      (value, context) => {
        if (context?.parent?.isCreateMode === false) return true;
        if (context?.parent?.paymentMode !== PAYMENT_MODE_PAID) return true;
        if (context?.parent?.paymentTypeId?.toString?.() !== "1") return true;
        if (!hasSelectedValue(value)) return true;
        const amount = parsePaymentAmountInput(value);
        return amount !== null && amount < 500;
      }
    ),
  reason: yup.string().nullable(),
  comments: yup.string().nullable(),
  remarks: yup.string().nullable(),
});

const initialFormValues = {
  isCreateMode: true,
  transType: "",
  initialTransType: "",
  transTypeName: "",
  transTypeSupplierMode: "",
  transDate: "",
  dateFromMode: "",
  dateToMode: "",
  dateFrom: "",
  dateTo: "",
  transNumber: "",
  supplierId: null,
  supplierAfm: "",
  supplierName: "",
  supplierCanEdit: false,
  supplierCommercialTitle: "",
  supplierDoy: "",
  supplierDoyDescr: "",
  street: "",
  streetNumber: "",
  postalCode: "",
  city: "",
  paymentMode: "",
  paymentTypeId: "",
  paymentAmount: "",
  reason: "",
  comments: "",
  remarks: "",
};

const getInitialTransType = (initialValues) =>
  initialValues?.transTypeId ??
  initialValues?.typeId ??
  initialValues?.transType ??
  "";

const getInitialFormValues = (initialValues, isCreateMode = true) => {
  const paidOnIssuePayment = getInitialPaidOnIssuePayment(initialValues);

  return {
    ...initialFormValues,
    isCreateMode,
    transType: getInitialTransType(initialValues),
    initialTransType: getInitialTransType(initialValues),
    transTypeName:
      initialValues?.transTypeName ?? initialValues?.transTypeDescr ?? "",
    transTypeSupplierMode: initialValues?.transTypeSupplierMode ?? "",
    transDate: initialValues?.transDate ?? "",
    dateFromMode: initialValues?.dateFromMode ?? "",
    dateToMode: initialValues?.dateToMode ?? "",
    dateFrom: initialValues?.dateFrom ?? "",
    dateTo: initialValues?.dateTo ?? "",
    transNumber: initialValues?.transNumber ?? "",
    supplierId: initialValues?.supplierId ?? null,
    supplierAfm: initialValues?.supplierAfm ?? "",
    supplierName: initialValues?.supplierName ?? "",
    supplierCanEdit: initialValues?.supplierCanEdit ?? false,
    supplierCommercialTitle:
      initialValues?.supplierCommercialTitle ?? initialValues?.commerTitle ?? "",
    supplierDoy: initialValues?.supplierDoy ?? initialValues?.doy ?? "",
    supplierDoyDescr:
      initialValues?.supplierDoyDescr ?? initialValues?.doyDescr ?? "",
    street: initialValues?.street ?? initialValues?.postalAddress ?? "",
    streetNumber:
      initialValues?.streetNumber ?? initialValues?.postalAddressNo ?? "",
    postalCode: initialValues?.postalCode ?? initialValues?.postalZipCode ?? "",
    city: initialValues?.city ?? initialValues?.postalAreaDescription ?? "",
    paymentMode: getInitialPaymentMode(initialValues),
    paymentTypeId: getInitialPaymentTypeId(initialValues, paidOnIssuePayment),
    paymentAmount: formatPaymentAmountForInput(
      getInitialPaymentAmount(initialValues, paidOnIssuePayment)
    ),
    reason: initialValues?.reason ?? initialValues?.description ?? "",
    comments: initialValues?.comments ?? "",
    remarks: initialValues?.remarks ?? "",
  };
};

const getTransTypeOptions = (res) => {
  const payload = getResponsePayload(res);

  if (!Array.isArray(payload)) return [];

  return payload.map((transType) => ({
    ...transType,
    value: transType.id,
    label: transType.descr ?? transType.abbr ?? "",
  }));
};

const getPaymentTypeOptions = (res) => {
  const payload = getResponsePayload(res);

  if (!Array.isArray(payload)) return [];

  return payload.map((paymentType) => ({
    ...paymentType,
    value: paymentType.id,
    label: paymentType.descr ?? paymentType.abbr ?? "",
  }));
};

const textInputSx = {
  "& .MuiInputBase-input": {
    fontSize: 15,
    py: 0.25,

  },
};

const expenseHeaderDialogPaperSx = {
  maxWidth: 760,
};

const transactionFormSurfaceSx = {
  width: "100%",
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "max-content minmax(0, 1fr)" },
  columnGap: 1,
  rowGap: 0.75,
  alignItems: "center",
};

const transactionFormRowSx = {
  display: { xs: "grid", sm: "contents" },
  gridTemplateColumns: "1fr",
  mb: { xs: 0.75, sm: 0 },
};

const transactionFormLabelSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: { xs: "flex-start", sm: "flex-end" },
  px: 1,
  color: "#111",
  fontSize: 18,
  fontStyle: "italic",
  lineHeight: 1.1,
  textAlign: { xs: "left", sm: "right" },
  whiteSpace: { sm: "nowrap" },
};

const transactionFormFieldSx = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  px: 1,
  py: 0.5,
  "& > *": {
    width: "100%",
  },
};

const paymentFieldsGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: "minmax(0, 1.35fr) minmax(150px, 0.65fr)",
  },
  alignItems: "start",
  columnGap: 1.5,
  rowGap: 0.75,
};

const paymentInlineFieldSx = {
  display: "grid",
  gridTemplateColumns: "1fr",
  minWidth: 0,
  rowGap: 0.25,
};

const paymentInlineLabelSx = {
  color: "#3f3f46",
  fontSize: 13,
  fontStyle: "italic",
  lineHeight: 1.15,
  pl: 0.25,
};

const TransactionFormRow = ({ label, children, minHeight = 34 }) => (
  <Box sx={transactionFormRowSx}>
    <Box
      sx={{
        ...transactionFormLabelSx,
        minHeight,
      }}
    >
      {label}:
    </Box>
    <Box
      sx={{
        ...transactionFormFieldSx,
        minHeight,
      }}
    >
      {children}
    </Box>
  </Box>
);

const mapSupplierToFormValues = (supplier) => ({
  supplierId: supplier?.id ?? null,
  supplierAfm: supplier?.afm ?? "",
  supplierName: supplier?.onomasia ?? supplier?.commerTitle ?? "",
  supplierCanEdit: supplier?.canEdit === true,
  supplierCommercialTitle: supplier?.commerTitle ?? "",
  supplierDoy: supplier?.doy ?? "",
  supplierDoyDescr: supplier?.doyDescr ?? "",
  street: supplier?.postalAddress ?? "",
  streetNumber: supplier?.postalAddressNo ?? "",
  postalCode: supplier?.postalZipCode ?? "",
  city: supplier?.postalAreaDescription ?? "",
});

const ExpenseHeaderDialog = ({
  open,
  mhdName = "ΜΗΔ xxx",
  isCreateMode = true,
  initialValues,
  isSaving = false,
  isTypeChangeAllowed = true,
  typeChangeMessage,
  onCancel,
  onSave,
}) => {
  const form = useForm({
    resolver: yupResolver(headerSchema),
    defaultValues: initialFormValues,
  });
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = form;
  const [transTypeOptions, setTransTypeOptions] = useState([]);
  const [isTransTypesLoading, setIsTransTypesLoading] = useState(false);
  const [hasLoadedTransTypes, setHasLoadedTransTypes] = useState(false);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [isPaymentTypesLoading, setIsPaymentTypesLoading] = useState(false);
  const [hasLoadedPaymentTypes, setHasLoadedPaymentTypes] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSupplierLookupLoading, setIsSupplierLookupLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [searchedSupplierText, setSearchedSupplierText] = useState("");
  const [hasSupplierSearchError, setHasSupplierSearchError] = useState(false);
  const [isSupplierSaving, setIsSupplierSaving] = useState(false);
  const [supplierDialogState, setSupplierDialogState] = useState({
    open: false,
    supplier: null,
    initialAfm: "",
    initialSupplierName: "",
  });

  const transType = watch("transType");
  const initialTransType = watch("initialTransType");
  const transTypeSupplierMode = watch("transTypeSupplierMode");
  const transDate = watch("transDate");
  const dateFromMode = watch("dateFromMode");
  const dateToMode = watch("dateToMode");
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");
  const supplierId = watch("supplierId");
  const supplierAfm = watch("supplierAfm");
  const supplierName = watch("supplierName");
  const supplierCanEdit = watch("supplierCanEdit");
  const supplierCommercialTitle = watch("supplierCommercialTitle");
  const supplierDoy = watch("supplierDoy");
  const supplierDoyDescr = watch("supplierDoyDescr");
  const street = watch("street");
  const streetNumber = watch("streetNumber");
  const postalCode = watch("postalCode");
  const city = watch("city");
  const paymentMode = watch("paymentMode");

  const effectiveTransType = hasSelectedValue(transType)
    ? transType
    : initialTransType;

  const selectedTransType = useMemo(
    () =>
      transTypeOptions.find(
        (option) => option.value?.toString() === effectiveTransType?.toString()
      ),
    [effectiveTransType, transTypeOptions]
  );
  const hasTransType = hasSelectedValue(effectiveTransType);
  const supplierMode = selectedTransType?.supplierMode ?? transTypeSupplierMode;
  const currentDateFromMode = selectedTransType?.dateFromMode ?? dateFromMode;
  const currentDateToMode = selectedTransType?.dateToMode ?? dateToMode;
  const isSupplierModeResolved =
    !hasTransType ||
    !!selectedTransType ||
    !!transTypeSupplierMode ||
    !isTransTypesLoading;
  const isSupplierVisible =
    hasTransType && isSupplierModeResolved && !isHiddenMode(supplierMode);
  const isSupplierRequired = isRequiredMode(supplierMode);
  const isDateFromVisible = hasTransType && isVisibleMode(currentDateFromMode);
  const isDateToVisible = hasTransType && isVisibleMode(currentDateToMode);
  const isPaidOnIssue = paymentMode === PAYMENT_MODE_PAID;
  const isPaymentDetailsDisabled = !isCreateMode;
  const isPaymentTypeDisabled =
    isPaymentDetailsDisabled ||
    isPaymentTypesLoading ||
    !paymentTypeOptions.length;
  const canChangeType = isCreateMode || isTypeChangeAllowed;
  const canEditSupplier =
    isSupplierVisible && !!supplierId;
  const selectedSupplier = useMemo(
    () =>
      supplierId
        ? {
          id: supplierId,
          afm: supplierAfm,
          onomasia: supplierName,
          commerTitle: supplierCommercialTitle,
          doy: supplierDoy,
          doyDescr: supplierDoyDescr,
          postalAddress: street,
          postalAddressNo: streetNumber,
          postalAreaDescription: city,
          postalZipCode: postalCode,
          canEdit: supplierCanEdit,
        }
        : null,
    [
      city,
      postalCode,
      street,
      streetNumber,
      supplierAfm,
      supplierCanEdit,
      supplierCommercialTitle,
      supplierDoy,
      supplierDoyDescr,
      supplierId,
      supplierName,
    ]
  );
  const supplierAutocompleteOptions = useMemo(() => {
    const options = Array.isArray(supplierOptions) ? [...supplierOptions] : [];

    if (
      selectedSupplier?.id &&
      !options.some(
        (option) => option.id?.toString() === selectedSupplier.id?.toString()
      )
    ) {
      options.unshift(selectedSupplier);
    }

    const currentSearch = normalizeSupplierSearch(supplierSearch);
    const canCreateFromSearch =
      currentSearch &&
      currentSearch === searchedSupplierText &&
      !isSupplierLookupLoading &&
      !hasSupplierSearchError &&
      !supplierOptions.length;

    if (canCreateFromSearch) {
      options.push(makeCreateSupplierOption(currentSearch));
    }

    return options;
  }, [
    hasSupplierSearchError,
    isSupplierLookupLoading,
    searchedSupplierText,
    selectedSupplier,
    supplierOptions,
    supplierSearch,
  ]);
  const isDialogReady =
    isFormInitialized &&
    hasLoadedTransTypes &&
    hasLoadedPaymentTypes;
  const handlePaymentAmountBlur = (event) => {
    setValue("paymentAmount", formatPaymentAmountForInput(event.target.value), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const getHeaderDatePickerProps = (fieldValue) => ({
    popperPlacement: "bottom-end",
    popperClassName: "expense-header-date-popper",
    customInput: <HeaderDatePickerButton />,
    onCalendarOpen: () => setIsDatePickerOpen(true),
    onCalendarClose: () => setIsDatePickerOpen(false),
    ...(hasSelectedValue(fieldValue) ? {} : { selected: null }),
    popperModifiers: [
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 8,
        },
      },
    ],
  });

  useEffect(() => {
    if (!open) {
      setIsFormInitialized(false);
      setIsDatePickerOpen(false);
      return;
    }

    reset(getInitialFormValues(initialValues, isCreateMode));
    setSupplierDialogState({
      open: false,
      supplier: null,
      initialAfm: "",
      initialSupplierName: "",
    });
    setSupplierOptions([]);
    setSupplierSearch("");
    setSearchedSupplierText("");
    setHasSupplierSearchError(false);
    setHasLoadedTransTypes(false);
    setPaymentTypeOptions([]);
    setHasLoadedPaymentTypes(false);
    setIsPaymentTypesLoading(false);
    setIsFormInitialized(true);
  }, [initialValues, isCreateMode, open, reset]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    const fetchTransTypes = async () => {
      setIsTransTypesLoading(true);
      setHasLoadedTransTypes(false);

      try {
        const res = await getTransTypes(false);
        if (!isActive) return;

        if (isErrorResponse(res)) {
          setTransTypeOptions([]);
          enqueueSnackbar(
            getResponseMessage(
              res,
              "Δεν φορτώθηκαν τα είδη παραστατικού."
            ),
            { variant: "warning" }
          );
          return;
        }

        setTransTypeOptions(getTransTypeOptions(res));
      } catch (error) {
        if (!isActive) return;

        setTransTypeOptions([]);
        enqueueSnackbar(
          error?.message ?? "Δεν φορτώθηκαν τα είδη παραστατικού.",
          { variant: "warning" }
        );
      } finally {
        if (isActive) {
          setIsTransTypesLoading(false);
          setHasLoadedTransTypes(true);
        }
      }
    };

    fetchTransTypes();

    return () => {
      isActive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    const fetchPaymentTypes = async () => {
      setIsPaymentTypesLoading(true);
      setHasLoadedPaymentTypes(false);

      try {
        const res = await getPaymentTypes();
        if (!isActive) return;

        if (isErrorResponse(res)) {
          setPaymentTypeOptions([]);
          enqueueSnackbar(
            getResponseMessage(
              res,
              "Δεν φορτώθηκαν οι τρόποι πληρωμής."
            ),
            { variant: "warning" }
          );
          return;
        }

        setPaymentTypeOptions(getPaymentTypeOptions(res));
      } catch (error) {
        if (!isActive) return;

        setPaymentTypeOptions([]);
        enqueueSnackbar(
          error?.message ?? "Δεν φορτώθηκαν οι τρόποι πληρωμής.",
          { variant: "warning" }
        );
      } finally {
        if (isActive) {
          setIsPaymentTypesLoading(false);
          setHasLoadedPaymentTypes(true);
        }
      }
    };

    fetchPaymentTypes();

    return () => {
      isActive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || paymentMode !== PAYMENT_MODE_CREDIT) return;

    setValue("paymentTypeId", "", {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue("paymentAmount", "", {
      shouldDirty: false,
      shouldValidate: false,
    });
    clearErrors(["paymentTypeId", "paymentAmount"]);
  }, [clearErrors, open, paymentMode, setValue]);

  useEffect(() => {
    if (!open || !isSupplierVisible) {
      setSupplierOptions([]);
      setSearchedSupplierText("");
      setHasSupplierSearchError(false);
      setIsSupplierLookupLoading(false);
      return undefined;
    }

    const currentSearch = normalizeSupplierSearch(supplierSearch);

    if (!currentSearch) {
      setSupplierOptions([]);
      setSearchedSupplierText("");
      setHasSupplierSearchError(false);
      setIsSupplierLookupLoading(false);
      return undefined;
    }

    setSupplierOptions([]);
    setSearchedSupplierText("");
    setHasSupplierSearchError(false);
    setIsSupplierLookupLoading(true);

    let isActive = true;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await searchSuppliers({
          search: currentSearch,
          pageNumber: 1,
          pageSize: 10,
        });
        if (!isActive) return;

        if (isErrorResponse(res)) {
          throw new Error(
            getResponseMessage(
              res,
              "Δεν ολοκληρώθηκε η αναζήτηση προμηθευτή."
            )
          );
        }

        const payload = getResponsePayload(res);
        setSupplierOptions(Array.isArray(payload) ? payload : []);
        setSearchedSupplierText(currentSearch);
      } catch (error) {
        if (!isActive) return;

        setSupplierOptions([]);
        setSearchedSupplierText("");
        setHasSupplierSearchError(true);
        enqueueSnackbar(
          error?.message ??
          "Προέκυψε σφάλμα κατά την αναζήτηση προμηθευτή.",
          { variant: "warning" }
        );
      } finally {
        if (isActive) {
          setIsSupplierLookupLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [isSupplierVisible, open, supplierSearch]);

  const clearSupplierValues = ({ keepAfm = "" } = {}) => {
    const clearedValues = {
      supplierId: null,
      supplierAfm: keepAfm,
      supplierName: "",
      supplierCanEdit: false,
      supplierCommercialTitle: "",
      supplierDoy: "",
      supplierDoyDescr: "",
      street: "",
      streetNumber: "",
      postalCode: "",
      city: "",
    };

    Object.entries(clearedValues).forEach(([fieldName, value]) => {
      setValue(fieldName, value, { shouldDirty: true });
    });
    clearErrors(["supplierAfm", "supplierId"]);
  };

  const handleTransTypeChange = (event) => {
    const selectedValue = event?.target?.value;
    const selectedOption = transTypeOptions.find(
      (option) => option.value?.toString() === selectedValue?.toString()
    );

    if (!isCreateMode || !selectedOption?.label) return;

    setValue("reason", selectedOption.label, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (!selectedTransType) return;

    setValue("transTypeName", selectedTransType.label ?? "", {
      shouldDirty: false,
    });
    setValue("transTypeSupplierMode", selectedTransType.supplierMode ?? "", {
      shouldDirty: false,
      shouldValidate: true,
    });
    setValue("dateFromMode", selectedTransType.dateFromMode ?? "", {
      shouldDirty: false,
      shouldValidate: true,
    });
    setValue("dateToMode", selectedTransType.dateToMode ?? "", {
      shouldDirty: false,
      shouldValidate: true,
    });
    clearErrors("transType");

    if (isHiddenMode(selectedTransType.supplierMode)) {
      clearSupplierValues();
    }
    if (isHiddenMode(selectedTransType.dateFromMode)) {
      setValue("dateFrom", "", { shouldDirty: false, shouldValidate: false });
      clearErrors("dateFrom");
    }
    if (isHiddenMode(selectedTransType.dateToMode)) {
      setValue("dateTo", "", { shouldDirty: false, shouldValidate: false });
      clearErrors("dateTo");
    }
  }, [selectedTransType, setValue]);

  const applySupplier = (supplier) => {
    const nextSupplierValues = mapSupplierToFormValues(supplier);

    Object.entries(nextSupplierValues).forEach(([fieldName, value]) => {
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: fieldName === "supplierId",
      });
    });
    clearErrors(["supplierAfm", "supplierId"]);
  };

  const openCreateSupplierDialog = (searchText = "") => {
    const normalizedSearch = normalizeSupplierSearch(searchText);
    const possibleAfm = normalizeAfm(normalizedSearch);
    const isAfmOnlySearch = /^\d{9}$/.test(normalizedSearch);

    clearSupplierValues();
    setSupplierDialogState({
      open: true,
      supplier: null,
      initialAfm: isAfmOnlySearch ? possibleAfm : "",
      initialSupplierName: isAfmOnlySearch ? "" : normalizedSearch,
    });
  };

  const handleSupplierInputChange = (_, value, reason) => {
    if (reason === "input") {
      setSupplierSearch(value);
      if (supplierId) {
        clearSupplierValues();
      }
      return;
    }

    if (reason === "clear") {
      setSupplierSearch("");
      clearSupplierValues();
    }
  };

  const handleSupplierChange = (_, option) => {
    if (isCreateSupplierOption(option)) {
      openCreateSupplierDialog(option.searchText);
      setSupplierSearch("");
      return;
    }

    if (!option) {
      setSupplierSearch("");
      clearSupplierValues();
      return;
    }

    applySupplier(option);
    setSupplierSearch("");
    setSupplierOptions((prevOptions) => {
      const exists = prevOptions.some(
        (supplierOption) =>
          supplierOption.id?.toString() === option.id?.toString()
      );

      return exists ? prevOptions : [option, ...prevOptions];
    });
  };

  const handleEditSupplier = () => {
    const values = getValues();

    setSupplierDialogState({
      open: true,
      initialAfm: values.supplierAfm,
      initialSupplierName: "",
      supplier: {
        id: values.supplierId,
        afm: values.supplierAfm,
        onomasia: values.supplierName,
        commerTitle: values.supplierCommercialTitle,
        doy: values.supplierDoy,
        doyDescr: values.supplierDoyDescr,
        postalAddress: values.street,
        postalAddressNo: values.streetNumber,
        postalAreaDescription: values.city,
        postalZipCode: values.postalCode,
      },
    });
  };

  const handleCancelSupplierDialog = () => {
    setSupplierDialogState({
      open: false,
      supplier: null,
      initialAfm: "",
      initialSupplierName: "",
    });
  };

  const handleSaveSupplier = async (supplierPayload) => {
    try {
      setIsSupplierSaving(true);
      const isEditMode = !!supplierPayload.id;
      const res = isEditMode
        ? await updateSupplier(supplierPayload)
        : await createSupplier(supplierPayload);

      if (isErrorResponse(res)) {
        enqueueSnackbar(
          getResponseMessage(res, "Δεν αποθηκεύτηκε ο προμηθευτής."),
          { variant: "error" }
        );
        return;
      }

      const supplier = getResponsePayload(res);
      applySupplier(supplier);
      setSupplierDialogState({
        open: false,
        supplier: null,
        initialAfm: "",
        initialSupplierName: "",
      });
      enqueueSnackbar("Ο προμηθευτής αποθηκεύτηκε.", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα κατά την αποθήκευση προμηθευτή.",
        { variant: "error" }
      );
    } finally {
      setIsSupplierSaving(false);
    }
  };

  const handleSave = (values) => {
    const { isCreateMode: _formIsCreateMode, ...saveValues } = values;
    const nextTransType = hasSelectedValue(values.transType)
      ? values.transType
      : values.initialTransType;
    const shouldPayOnIssue = values.paymentMode === PAYMENT_MODE_PAID;

    onSave?.({
      ...saveValues,
      transType: nextTransType,
      transTypeName: selectedTransType?.label ?? values.transTypeName,
      transTypeSupplierMode: supplierMode,
      dateFromMode: currentDateFromMode,
      dateToMode: currentDateToMode,
      dateFrom: isDateFromVisible ? values.dateFrom : null,
      dateTo: isDateToVisible ? values.dateTo : null,
      supplierId: isSupplierVisible ? values.supplierId : null,
      paidOnIssue: shouldPayOnIssue,
      paymentTypeId: shouldPayOnIssue ? values.paymentTypeId : null,
      paymentAmount: shouldPayOnIssue ? values.paymentAmount : null,
    });
  };

  return (
    <FormProvider {...form}>
      <GlobalStyles
        styles={{
          ".expense-header-date-popper": {
            zIndex: "1500 !important",
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onCancel}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: expenseHeaderDialogPaperSx }}
        aria-labelledby="expense-header-dialog-title"
        disableEnforceFocus={isDatePickerOpen}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              width: "100%",
              mx: "auto",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                py: 0.5,
                px: 1,
                textAlign: "center",
              }}
            >
              <Typography
                id="expense-header-dialog-title"
                component="h2"
                sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}
              >
                {mhdName}
              </Typography>
            </Box>

            {!canChangeType && typeChangeMessage && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {typeChangeMessage}
              </Alert>
            )}

            {!isDialogReady ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Φόρτωση στοιχείων παραστατικού...
              </Alert>
            ) : (
              <Box sx={transactionFormSurfaceSx}>
                <TransactionFormRow label="Είδος Παραστατικού">
                  <Dropdown
                    name="transType"
                    label={null}
                    options={transTypeOptions}
                    valueName="value"
                    labelName="label"
                    disabled={!canChangeType}
                    changeFn={handleTransTypeChange}
                    fixedPadding={4}
                  />
                </TransactionFormRow>

                <TransactionFormRow label="Ημ/νία">
                  <Box
                    sx={{
                      width: "100%",
                      "& > .MuiStack-root": { width: "100%" },
                    }}
                  >
                    <DateInput
                      name="transDate"
                      label={null}
                      variant="standard"
                      isFullWidth
                      blurFn={() => clearErrors("transDate")}
                      fixedPadding={4}
                      sx={{ ...textInputSx, width: "100%" }}
                      datePickerProps={getHeaderDatePickerProps(transDate)}
                    />
                  </Box>
                </TransactionFormRow>

                {isDateFromVisible && (
                  <TransactionFormRow
                    label={`Ημ/νία Από${isRequiredMode(currentDateFromMode) ? " *" : ""}`}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        "& > .MuiStack-root": { width: "100%" },
                      }}
                    >
                      <DateInput
                        name="dateFrom"
                        label={null}
                        variant="standard"
                        isFullWidth
                        blurFn={() => clearErrors("dateFrom")}
                        fixedPadding={4}
                        sx={{ ...textInputSx, width: "100%" }}
                        datePickerProps={getHeaderDatePickerProps(dateFrom)}
                      />
                    </Box>
                  </TransactionFormRow>
                )}

                {isDateToVisible && (
                  <TransactionFormRow
                    label={`Ημ/νία Έως${isRequiredMode(currentDateToMode) ? " *" : ""}`}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        "& > .MuiStack-root": { width: "100%" },
                      }}
                    >
                      <DateInput
                        name="dateTo"
                        label={null}
                        variant="standard"
                        isFullWidth
                        blurFn={() => clearErrors("dateTo")}
                        fixedPadding={4}
                        sx={{ ...textInputSx, width: "100%" }}
                        datePickerProps={getHeaderDatePickerProps(dateTo)}
                      />
                    </Box>
                  </TransactionFormRow>
                )}

                <TransactionFormRow label="Αριθμός">
                  <TextInput
                    name="transNumber"
                    label={null}
                    variant="standard"
                    fixedPadding={4}
                    sx={textInputSx}
                    fullWidth
                  />
                </TransactionFormRow>

                {isSupplierVisible && (
                  <>
                    <TransactionFormRow
                      label={`Προμηθευτής${isSupplierRequired ? " *" : ""}`}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            sm: canEditSupplier
                              ? "minmax(0, 1fr) auto"
                              : "minmax(0, 1fr)",
                          },
                          gap: { xs: 0.5, sm: 0.75 },
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Controller
                            name="supplierId"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                options={supplierAutocompleteOptions}
                                value={selectedSupplier}
                                onChange={(event, option) => {
                                  field.onChange(
                                    isCreateSupplierOption(option)
                                      ? null
                                      : option?.id ?? null
                                  );
                                  handleSupplierChange(event, option);
                                }}
                                onInputChange={handleSupplierInputChange}
                                onBlur={() => {
                                  field.onBlur();
                                  trigger("supplierId");
                                }}
                                loading={isSupplierLookupLoading}
                                filterOptions={(options) => options}
                                noOptionsText={
                                  normalizeSupplierSearch(supplierSearch)
                                    ? "Δεν βρέθηκαν προμηθευτές"
                                    : "Πληκτρολογήστε για αναζήτηση"
                                }
                                loadingText="Αναζήτηση..."
                                clearText="Καθαρισμός"
                                openText="Άνοιγμα"
                                closeText="Κλείσιμο"
                                isOptionEqualToValue={(option, value) =>
                                  option.id?.toString() ===
                                  value?.id?.toString()
                                }
                                getOptionLabel={getSupplierOptionLabel}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    {isCreateSupplierOption(option) ? (
                                      <Typography
                                        sx={{ fontSize: 13, fontWeight: 800 }}
                                      >
                                        {getSupplierOptionLabel(option)}
                                      </Typography>
                                    ) : (
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          sx={{ fontSize: 13, fontWeight: 700 }}
                                        >
                                          {option.onomasia ||
                                            option.commerTitle ||
                                            "Προμηθευτής"}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: "text.secondary",
                                            fontSize: 12,
                                          }}
                                        >
                                          {[option.afm, option.commerTitle]
                                            .filter(Boolean)
                                            .join(" - ")}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <CssTextField
                                    {...params}
                                    fullWidth
                                    variant="standard"
                                    error={!!errors.supplierId}
                                    helperText={errors.supplierId?.message}
                                    InputProps={{
                                      ...params.InputProps,
                                      disableUnderline: true,
                                      endAdornment: (
                                        <>
                                          {isSupplierLookupLoading && (
                                            <CircularProgress
                                              color="inherit"
                                              size={18}
                                            />
                                          )}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                    fixedPadding={4}
                                    sx={textInputSx}
                                  />
                                )}
                              />
                            )}
                          />
                        </Box>
                        {canEditSupplier && (
                          <Tooltip title="Επεξεργασία στοιχείων προμηθευτή">
                            <IconButton
                              aria-label="Επεξεργασία στοιχείων προμηθευτή"
                              size="small"
                              color="primary"
                              onClick={handleEditSupplier}
                              sx={{
                                alignSelf: "flex-start",
                                height: 28,
                                justifySelf: { xs: "flex-end", sm: "end" },
                                mt: 0.25,
                                opacity: 0.72,
                                width: 28,
                                "&:hover": {
                                  opacity: 1,
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TransactionFormRow>

                    <TransactionFormRow label="Επωνυμία">
                      <TextInput
                        name="supplierName"
                        label={null}
                        variant="standard"
                        disabled
                        fixedPadding={4}
                        sx={textInputSx}
                      />
                    </TransactionFormRow>

                    <TransactionFormRow label="Δ/νση" minHeight={70}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) 120px",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <TextInput
                          name="street"
                          label={null}
                          variant="standard"
                          placeholder="Οδός"
                          disabled
                          fixedPadding={4}
                          sx={textInputSx}
                        />
                        <TextInput
                          name="streetNumber"
                          label={null}
                          variant="standard"
                          placeholder="Αριθμός"
                          disabled
                          fixedPadding={4}
                          sx={textInputSx}
                        />
                        <TextInput
                          name="postalCode"
                          label={null}
                          variant="standard"
                          placeholder="ΤΚ"
                          disabled
                          fixedPadding={4}
                          sx={textInputSx}
                        />
                        <TextInput
                          name="city"
                          label={null}
                          variant="standard"
                          placeholder="Πόλη"
                          disabled
                          fixedPadding={4}
                          sx={textInputSx}
                        />
                      </Box>
                    </TransactionFormRow>
                  </>
                )}

                <TransactionFormRow label="Αιτιολογία">
                  <TextInput
                    name="reason"
                    label={null}
                    variant="standard"
                    fixedPadding={4}
                    sx={textInputSx}
                  />
                </TransactionFormRow>

                <TransactionFormRow label="Τρόπος Εξόφλησης">
                  <RadioInput
                    name="paymentMode"
                    label={null}
                    options={PAYMENT_MODE_OPTIONS}
                    changeFn={() =>
                      clearErrors(["paymentMode", "paymentTypeId", "paymentAmount"])
                    }
                  />
                </TransactionFormRow>

                {isPaidOnIssue && (
                  <TransactionFormRow label="Πληρωμή" minHeight={58}>
                    <Box sx={paymentFieldsGridSx}>
                      <Box sx={paymentInlineFieldSx}>
                        <Typography sx={paymentInlineLabelSx}>
                          Τρόπος *
                        </Typography>
                        <Dropdown
                          name="paymentTypeId"
                          label={null}
                          options={paymentTypeOptions}
                          valueName="value"
                          labelName="label"
                          disabled={isPaymentTypeDisabled}
                          placeholder={
                            isPaymentTypesLoading
                              ? "Φόρτωση τρόπων πληρωμής..."
                              : "Επιλέξτε τρόπο πληρωμής"
                          }
                          changeFn={() => clearErrors("paymentTypeId")}
                          fixedPadding={4}
                          sx={textInputSx}
                        />
                      </Box>

                      <Box sx={paymentInlineFieldSx}>
                        <Typography sx={paymentInlineLabelSx}>
                          Ποσό *
                        </Typography>
                        <CurrencyInput
                          name="paymentAmount"
                          label={null}
                          variant="standard"
                          inputAlign="right"
                          disabled={isPaymentDetailsDisabled}
                          defaultFromProps={false}
                          fixedPadding={4}
                          sx={textInputSx}
                          blurFn={handlePaymentAmountBlur}
                        />
                      </Box>
                    </Box>
                  </TransactionFormRow>
                )}

                <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: 1, ml: "auto", width: "100%" }}>
                  <CustomSimpleAccordion
                    title={
                      <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                        Σχόλια - Παρατηρήσεις
                      </Typography>
                    }
                    content={
                      <Box sx={transactionFormSurfaceSx}>
                        <TransactionFormRow label="Σχόλια">
                          <TextInput
                            name="comments"
                            label={null}
                            variant="standard"
                            fixedPadding={4}
                            sx={textInputSx}
                          />
                        </TransactionFormRow>

                        <TransactionFormRow label="Παρατηρήσεις">
                          <TextInput
                            name="remarks"
                            label={null}
                            variant="standard"
                            fixedPadding={4}
                            sx={textInputSx}
                          />
                        </TransactionFormRow>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>

          {isCreateMode && isDialogReady && (
            <Box sx={{ width: "100%", maxWidth: 760, mx: "auto", mt: 2 }}>
              <ExpenseAttachments
                settings={CREATE_HEADER_ATTACHMENT_SETTINGS}
                isSaving={isSaving}
                showSaveButton={false}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 2,
              rowGap: 1,
              width: "100%",
              maxWidth: 760,
              mx: "auto",
              mt: 5,
            }}
          >
            <Button variant="contained" color="error" onClick={onCancel}>
              Άκυρο
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(handleSave)}
              disabled={isSaving || !isDialogReady}
            >
              Αποθήκευση
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <SupplierDialog
        open={supplierDialogState.open}
        supplier={supplierDialogState.supplier}
        initialAfm={supplierDialogState.initialAfm}
        initialSupplierName={supplierDialogState.initialSupplierName}
        isSaving={isSupplierSaving}
        onCancel={handleCancelSupplierDialog}
        onSave={handleSaveSupplier}
      />
    </FormProvider>
  );
};

export default ExpenseHeaderDialog;
